// @ts-nocheck
/*
 * Port of views/dashboard/agent-activity/ActivityTimeline.js (575 lines).
 * BYTE-IDENTICAL — gamified activity summary + framer-motion timeline with
 * per-event icon/color mapping, recursive DetailList/DetailValue for
 * arbitrary event `details` payloads, and a "search-across-anything" filter.
 */
import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { motion } from 'framer-motion';
import {
  LogIn,
  LogOut,
  Phone,
  Coffee,
  MousePointerClick,
  Compass,
  Search,
  AlertTriangle,
  Sparkles,
  Activity,
  Clock,
  Zap,
  Trophy,
  X,
} from 'lucide-react';

const fmtTime = (v) => (v ? moment(v).format('HH:mm:ss') : '-');

const styleForType = (rawType = '', custom = false) => {
  const t = String(rawType).toLowerCase();
  const has = (...keys) => keys.some((k) => t.includes(k));

  if (custom || has('custom')) return { Icon: Sparkles, color: '#b45309', tint: '#fef3c7', label: 'Custom' };
  if (has('login', 'sign_in', 'signin')) return { Icon: LogIn, color: '#047857', tint: '#d1fae5', label: 'Login' };
  if (has('logout', 'sign_out', 'signout')) return { Icon: LogOut, color: '#475569', tint: '#e2e8f0', label: 'Logout' };
  if (has('call', 'dial', 'ring')) return { Icon: Phone, color: '#1d4ed8', tint: '#dbeafe', label: 'Call' };
  if (has('break', 'pause', 'idle')) return { Icon: Coffee, color: '#b45309', tint: '#fef3c7', label: 'Break' };
  if (has('click', 'button', 'tap')) return { Icon: MousePointerClick, color: '#4f46e5', tint: '#e0e7ff', label: 'Click' };
  if (has('page', 'nav', 'view', 'route', 'visit')) return { Icon: Compass, color: '#7c3aed', tint: '#ede9fe', label: 'Navigate' };
  if (has('search', 'filter', 'query')) return { Icon: Search, color: '#0891b2', tint: '#cffafe', label: 'Search' };
  if (has('error', 'fail', 'denied', 'reject')) return { Icon: AlertTriangle, color: '#dc2626', tint: '#fee2e2', label: 'Alert' };
  return { Icon: Activity, color: '#64748b', tint: '#f1f5f9', label: 'Event' };
};

const intensityTier = (count) => {
  if (count >= 60) return { label: 'On fire', emoji: '🔥', color: '#dc2626', pct: 100 };
  if (count >= 30) return { label: 'Busy', emoji: '⚡', color: '#ea580c', pct: 80 };
  if (count >= 12) return { label: 'Active', emoji: '✨', color: '#4f46e5', pct: 55 };
  if (count >= 1) return { label: 'Warming up', emoji: '🌱', color: '#059669', pct: 28 };
  return { label: 'Quiet', emoji: '💤', color: '#94a3b8', pct: 6 };
};

const Stat = ({ Icon, value, label, color }: any) => (
  <div
    className="flex min-w-0 flex-1 items-center gap-2.5 rounded-xl border border-border/60 bg-card px-3.5 py-2.5"
    style={{
      boxShadow:
        '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
    }}
  >
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 34,
        height: 34,
        borderRadius: 10,
        background: `${color}14`,
        color,
        flexShrink: 0,
      }}
    >
      <Icon size={17} strokeWidth={2.2} />
    </span>
    <div className="min-w-0">
      <div className="text-base font-bold leading-tight text-foreground">{value}</div>
      <div className="whitespace-nowrap text-[11px] font-medium text-muted-foreground">
        {label}
      </div>
    </div>
  </div>
);

function SessionSummary({ events }: any) {
  const count = events.length;
  const tier = intensityTier(count);
  const times = events.map((e) => e.time).filter(Boolean).map((t) => moment(t));
  const first = times.length ? moment.min(times) : null;
  const last = times.length ? moment.max(times) : null;
  const span = first && last && last.diff(first) > 0 ? moment.duration(last.diff(first)).humanize() : '—';
  const uniqueTypes = new Set(events.map((e) => String(e.type || '').toLowerCase())).size;

  return (
    <div className="mb-4">
      <div
        className="mb-3 flex items-center gap-3.5 rounded-2xl border border-border/60 bg-card px-4 py-3.5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <span
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-muted/60 text-2xl ring-1 ring-inset ring-border/60"
          aria-hidden
        >
          {tier.emoji}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Trophy size={14} color={tier.color} strokeWidth={2.4} />
            <span className="text-sm font-bold" style={{ color: tier.color }}>
              {tier.label}
            </span>
            <span className="text-xs font-medium text-muted-foreground">
              · activity level
            </span>
          </div>
          <div
            className="relative mt-2 h-2 overflow-hidden rounded-full bg-muted/60"
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${tier.pct}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              style={{
                height: '100%',
                borderRadius: 999,
                background: `linear-gradient(90deg, ${tier.color}, ${tier.color}cc)`,
              }}
            />
            <motion.div
              initial={{ x: '-130%' }}
              animate={{ x: '360%' }}
              transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.5 }}
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                width: '35%',
                transform: 'skewX(-22deg)',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.85), transparent)',
                pointerEvents: 'none',
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2.5">
        <Stat Icon={Zap} value={count} label="Total events" color="#4f46e5" />
        <Stat Icon={Activity} value={uniqueTypes} label="Action types" color="#7c3aed" />
        <Stat Icon={Clock} value={first ? first.format('HH:mm') : '—'} label="First event" color="#0891b2" />
      </div>
    </div>
  );
}

const prettyKey = (k) =>
  String(k)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

function DetailValue({ value, accent, tint }: any) {
  if (value === null || value === undefined || value === '') return <span style={{ fontWeight: 600 }}>—</span>;
  if (typeof value === 'boolean')
    return (
      <span
        style={{
          fontSize: 11.5,
          fontWeight: 700,
          padding: '1px 8px',
          borderRadius: 999,
          color: value ? '#047857' : '#b91c1c',
          background: value ? '#d1fae5' : '#fee2e2',
        }}
      >
        {value ? 'Yes' : 'No'}
      </span>
    );
  if (Array.isArray(value))
    return (
      <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
        {value.length === 0 ? (
          <span style={{ fontWeight: 600 }}>—</span>
        ) : (
          value.map((item, i) => <DetailValue key={i} value={item} accent={accent} tint={tint} />)
        )}
      </span>
    );
  if (isPlainObject(value))
    return (
      <span style={{ display: 'block', marginTop: 4 }}>
        <DetailList details={value} accent={accent} tint={tint} nested />
      </span>
    );
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        borderRadius: 7,
        padding: '2px 9px',
        wordBreak: 'break-word',
      }}
    >
      {String(value)}
    </span>
  );
}

function DetailList({ details, accent, tint, nested = false }: any) {
  const entries = Object.entries(details || {});
  if (entries.length === 0) return null;

  return (
    <div
      className={
        nested
          ? 'flex flex-col gap-1.5'
          : 'mt-2 flex flex-col gap-2 rounded-lg border border-border/50 bg-muted/30 px-3 py-2.5'
      }
    >
      {entries.map(([k, v]) => (
        <div
          key={k}
          className="flex flex-wrap items-baseline gap-2.5"
        >
          <span className="min-w-[96px] shrink-0 text-[11px] font-semibold text-muted-foreground">
            {prettyKey(k)}
          </span>
          <span className="min-w-0 flex-1 text-foreground">
            <DetailValue value={v} accent={accent} tint={tint} />
          </span>
        </div>
      ))}
    </div>
  );
}

function TimelineEvent({ ev, idx, isLast, total }: any) {
  const { Icon, color, tint, label } = styleForType(ev.type, ev.custom);
  const hasDetails = ev.details && Object.keys(ev.details).length > 0;
  const isFinalApproach = !isLast && idx === total - 2 && total >= 3;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(idx * 0.025, 0.4) }}
      style={{ display: 'flex', gap: 14, position: 'relative' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: tint,
            color,
            border: '2px solid #ffffff65',
            zIndex: 1,
          }}
        >
          <Icon size={15} strokeWidth={2.3} />
        </span>
        {!isLast && (
          <span
            className={'atl-rail' + (isFinalApproach ? ' atl-rail-red' : '')}
            style={{
              flex: 1,
              width: 3,
              marginTop: 2,
              borderRadius: 3,
              backgroundImage: isFinalApproach
                ? 'linear-gradient(180deg, #fecaca 0%, #ef4444 45%, #fecaca 90%)'
                : 'linear-gradient(180deg, #e0e7ff 0%, ' + color + ' 45%, #e0e7ff 90%)',
              backgroundSize: '100% 220%',
            }}
          />
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 0 : 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.2,
              textTransform: 'uppercase',
              color,
              background: tint,
              borderRadius: 6,
              padding: '2px 8px',
            }}
          >
            {label}
          </span>
          <code style={{ fontSize: 12.5, background: 'transparent', padding: 0 }}>{ev.type}</code>
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 500 }}>
            <Clock size={12} />
            {fmtTime(ev.time)}
          </span>
        </div>
        {hasDetails && <DetailList details={ev.details} accent={color} tint={tint} />}
      </div>
    </motion.div>
  );
}

function EmptyState({ message }: any) {
  return (
    <div className="flex flex-col items-center gap-2.5 rounded-xl border border-border/60 bg-muted/30 px-5 py-10">
      <span className="inline-flex h-13 w-13 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-inset ring-primary/20">
        <Activity size={24} strokeWidth={2} />
      </span>
      <div className="text-sm font-medium text-muted-foreground">{message}</div>
    </div>
  );
}

const eventHaystack = (ev) => {
  const { label } = styleForType(ev.type, ev.custom);
  return [
    ev.type,
    label,
    fmtTime(ev.time),
    ev.time,
    ev.custom ? 'custom' : '',
    ev.details ? JSON.stringify(ev.details) : '',
  ]
    .join(' ')
    .toLowerCase();
};

export default function ActivityTimeline({ events, showSummary = true, emptyMessage = 'No events recorded for this session.' }: any) {
  const [query, setQuery] = useState('');
  const list = Array.isArray(events) ? events : [];

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => (q ? list.filter((ev) => eventHaystack(ev).includes(q)) : list), [q, list]);

  if (list.length === 0) return <EmptyState message={emptyMessage} />;

  return (
    <div>
      <style>{`
        @keyframes atl-flow {
          0%   { background-position: 50% 0%; }
          100% { background-position: 50% -110%; }
        }
        .atl-rail { animation: atl-flow 2.4s linear infinite; }
        .atl-rail-red { animation-duration: 1.3s; }
        @media (prefers-reduced-motion: reduce) {
          .atl-rail, .atl-rail-red { animation: none; }
        }
      `}</style>
      {showSummary && <SessionSummary events={list} />}

      <div className="relative mb-3 flex items-center">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search activity…"
          className="input-embossed h-10 w-full rounded-md pl-3 pr-9 text-sm"
        />
        {query ? (
          <button
            type="button"
            onClick={() => setQuery('')}
            aria-label="Clear search"
            className="absolute right-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground cursor-pointer"
          >
            <X size={13} strokeWidth={2.6} />
          </button>
        ) : (
          <span className="pointer-events-none absolute right-3 inline-flex text-muted-foreground">
            <Search size={15} strokeWidth={2.4} />
          </span>
        )}
      </div>

      {q && (
        <div className="mb-2.5 text-xs font-medium text-muted-foreground">
          <span className="font-semibold text-foreground">{filtered.length}</span> of{' '}
          <span className="font-semibold text-foreground">{list.length}</span>{' '}
          {list.length === 1 ? 'event' : 'events'}
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState message={`No activity matches “${query}”.`} />
      ) : (
        <div>
          {filtered.map((ev, idx) => (
            <TimelineEvent key={idx} ev={ev} idx={idx} total={filtered.length} isLast={idx === filtered.length - 1} />
          ))}
        </div>
      )}
    </div>
  );
}
